// Banking: each bank line from the feed or a statement waits here until it is matched to what the
// books already hold (a deposit, a vendor payment), or categorised to an account (with a rule for
// next time). Reconcile against the statement when the difference is zero. The last finished
// reconciliation can be reopened, with a reason, to correct a matched line (bank.reopenReconciliation).

import { useEffect, useMemo, useState } from 'react';
import { holdsAny } from '../shared/may.ts';
import { RpcError } from '@flowology/client';
import { Figure } from '@flowology/ui-web';
import { client } from '../api.ts';
import { useDesign } from '../blueprint/design.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import type { BooksProps } from './props.ts';
import { dayText, money, parseMoney, readUntil, todayIn } from './model.ts';
import type { Account, BankAccount, BankLine, Reconciliation, Suggestions } from './types.ts';
import {
  Empty,
  Loading,
  Money,
  Panel,
  RowActions,
  errorText,
  useAct,
  useQuery,
  useRowPrimary,
} from './ui.tsx';
import { DocLink } from '../shared/DocLink.tsx';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { CLOSE_PERMS, reopenOutcome } from './corrections-model.ts';
import { ImportStatement, RecordDeposit } from './BankingEntry.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';

const CONTROL = new Set(['accounts_receivable', 'accounts_payable', 'customer_credits']);
const isControl = (a: Account) => (a.roles ?? []).some((r) => CONTROL.has(r.role));

function LineCard({
  line,
  accounts,
  taxCode,
  act,
  toast,
  reload,
}: {
  line: BankLine;
  accounts: Account[];
  /** The recoverable tax a purchase carries here (GST or HST), split out of money out (QA-810). */
  taxCode: string;
  act: ReturnType<typeof useAct>;
  toast: (m: string) => void;
  reload: () => Promise<unknown>;
}) {
  const [sugg, setSugg] = useState<Suggestions | null>(null);
  const [mode, setMode] = useState<'idle' | 'categorise'>('idle');
  const [accountId, setAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [tax, setTax] = useState('');
  const [rule, setRule] = useState(false);
  const rowPrimary = useRowPrimary();
  // The server holds back a line that looks like something already in the books (possible_duplicate):
  // its words say which, and the person can still say it's different.
  const [dupe, setDupe] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const look = async () => {
    try {
      setSugg(await client.call<Suggestions>('bank.suggestMatches', { bankTransactionId: line.id }));
    } catch (e) {
      toast(errorText(e));
    }
  };
  const best = sugg?.matches[0];
  const categorise = async (notDuplicate = false) => {
    if (!accountId) return toast('Pick the account it belongs to.');
    const taxCents = line.amountCents < 0 && tax.trim() ? parseMoney(tax) : 0;
    if (taxCents === null || taxCents < 0) return toast(`Type the ${taxCode} in it, like 20.32.`);
    setPosting(true);
    let ok = false;
    try {
      await client.call('bank.categorise', {
        bankTransactionId: line.id,
        accountId,
        ...(memo ? { memo } : {}),
        ...(taxCents ? { taxes: [{ code: taxCode, taxCents }] } : {}),
        ...(notDuplicate ? { notDuplicate: true } : {}),
      });
      ok = true;
      setDupe(null);
      toast('Categorised and posted.');
      await reload();
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'possible_duplicate') setDupe(errorText(e));
      else toast(errorText(e));
    } finally {
      setPosting(false);
    }
    if (ok && rule)
      await act.run(
        'bank.createRule',
        { contains: line.description, direction: line.amountCents < 0 ? 'out' : 'in', accountId },
        'Rule saved: lines like this are categorised the same way.',
      );
  };

  return (
    <li className="stone line-card">
      <div className="line-top">
        <div>
          <strong>{line.description}</strong>
          <span className="muted small">
            {dayText(line.postedOn)}
            {line.reference ? ` · ${line.reference}` : ''}
          </span>
        </div>
        <Money cents={line.amountCents} signed className="amount" />
      </div>
      {!sugg && mode === 'idle' && (
        <RowActions>
          <button className="btn soft" onClick={() => void look()}>
            Find a match
          </button>
          <button className="btn quiet" onClick={() => setMode('categorise')}>
            Categorise
          </button>
        </RowActions>
      )}
      {sugg && mode === 'idle' && (
        <div className="suggest">
          {best ? (
            <>
              <p>
                <span className="chip say brand">Suggested match</span> {best.why}{' '}
                <span className="muted">
                  (<DocLink doc={{ kind: 'journal-entry', id: best.entryId }}>{best.entryNumber}</DocLink>)
                </span>
              </p>
              <RowActions>
                <button
                  className={rowPrimary}
                  disabled={act.busy}
                  onClick={() =>
                    void act.run(
                      'bank.match',
                      { bankTransactionId: line.id, journalLineIds: best.journalLineIds },
                      'Matched.',
                    )
                  }
                >
                  Accept match
                </button>
                <button className="btn quiet" onClick={() => setMode('categorise')}>
                  Something else
                </button>
              </RowActions>
            </>
          ) : (
            <>
              <p className="muted">No match found. Nothing open in the books looks like this line.</p>
              <button className="btn soft" onClick={() => setMode('categorise')}>
                Categorise
              </button>
            </>
          )}
        </div>
      )}
      {mode === 'categorise' && (
        <div className="fgrid categorise">
          <label>
            <span className="flabel">Account</span>
            <select className="field" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Choose…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="flabel">Memo</span>
            <input
              className="field"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Optional"
            />
          </label>
          {line.amountCents < 0 && (
            <label>
              <span className="flabel">{taxCode} in it (recoverable)</span>
              <input
                className="field"
                inputMode="decimal"
                value={tax}
                onChange={(e) => setTax(e.target.value)}
                placeholder="0.00"
              />
            </label>
          )}
          <label className="checkrow">
            <input type="checkbox" checked={rule} onChange={(e) => setRule(e.target.checked)} /> Make a rule
            for lines like “{line.description}”
          </label>
          {dupe && (
            <div className="notice warn full" role="alert">
              <p>{dupe}</p>
              <div className="row-actions">
                <button
                  className="btn"
                  disabled={posting}
                  onClick={() => {
                    setDupe(null);
                    setMode('idle');
                    void look();
                  }}
                >
                  Find a match
                </button>
                <button className="btn quiet" disabled={posting} onClick={() => void categorise(true)}>
                  It’s different, categorise it
                </button>
              </div>
            </div>
          )}
          <div className="row-actions">
            <button className="btn primary" disabled={act.busy || posting} onClick={() => void categorise()}>
              Categorise
            </button>
            <button
              className="btn quiet"
              onClick={() => {
                setDupe(null);
                setMode('idle');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function ReconcilePanel({
  bank,
  act,
  toast,
  mayReopen,
  onChanged,
}: {
  bank: BankAccount;
  act: ReturnType<typeof useAct>;
  /** accounting.close: reopen the last finished reconciliation. */
  mayReopen: boolean;
  onChanged: () => Promise<unknown>;
  toast: (m: string) => void;
}) {
  const [end, setEnd] = useState(todayIn());
  const [closing, setClosing] = useState('');
  // The account's first reconciliation starts from the statement's own opening (QA-801).
  const first = !bank.reconciledThrough;
  const [opening, setOpening] = useState('');
  const [rec, setRec] = useState<Reconciliation | null>(null);
  const [reopening, setReopening] = useState(false);
  /** Changing the statement of the one in progress (its end date or closing balance). */
  const [changing, setChanging] = useState(false);
  // The one in progress is the account's until it's finished: carry on with it (QA-800).
  const openId = bank.openReconciliation?.id ?? null;
  useEffect(() => {
    if (!openId) return;
    let live = true;
    void client
      .call<Reconciliation>('bank.reconciliationStatus', { reconciliationId: openId })
      .then((r) => {
        if (live) setRec((now) => now ?? r);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [openId]);
  const start = async () => {
    const cents = parseMoney(closing);
    if (cents === null) return toast('Type the statement’s closing balance, like 12,345.67.');
    const openingCents = first && opening.trim() ? parseMoney(opening) : undefined;
    if (openingCents === null) return toast('Type the statement’s opening balance, like 12,345.67.');
    const r = await act.run<Reconciliation>('bank.startReconciliation', {
      bankAccountId: bank.id,
      statementEndDate: end,
      closingCents: cents,
      ...(openingCents === undefined ? {} : { openingCents }),
    });
    if (r) {
      setRec(r);
      setChanging(false);
    }
  };
  const change = () => {
    if (!rec) return;
    setEnd(rec.statementEndDate.slice(0, 10));
    setClosing((rec.closingCents / 100).toFixed(2));
    if (first) setOpening((rec.openingCents / 100).toFixed(2));
    setChanging(true);
  };
  const refresh = async () => {
    if (rec)
      setRec(await client.call<Reconciliation>('bank.reconciliationStatus', { reconciliationId: rec.id }));
  };
  return (
    <Panel
      title="Reconciliation"
      aside={
        rec ? (
          <span className={`chip say ${rec.status === 'finished' ? 'ok' : ''}`}>
            {rec.status === 'finished' ? 'Finished' : 'In progress'}
          </span>
        ) : undefined
      }
    >
      <p className="muted small">
        {bank.reconciledThrough
          ? `Reconciled through ${dayText(bank.reconciledThrough)}.`
          : 'Not reconciled yet.'}
      </p>
      {!rec && mayReopen && bank.lastReconciliation && (
        <div className="row-actions">
          <button className="btn soft" type="button" onClick={() => setReopening(true)}>
            Reopen the last reconciliation
          </button>
        </div>
      )}
      {reopening && bank.lastReconciliation && (
        <ReopenReconciliation
          id={bank.lastReconciliation.id}
          through={dayText(bank.lastReconciliation.through)}
          onClose={() => setReopening(false)}
          onDone={async () => {
            setReopening(false);
            toast('Reopened: its lines are unlocked. Start a new reconciliation to finish it again.');
            await onChanged();
          }}
        />
      )}
      {rec && rec.status === 'in_progress' && !changing && (
        <p className="muted small">
          {`In progress, against the statement to ${dayText(rec.statementEndDate.slice(0, 10))}.`}
        </p>
      )}
      {!rec || changing ? (
        <div className="fgrid">
          <label>
            <span className="flabel">Statement ends</span>
            <input className="field" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
          {first && (
            <label>
              <span className="flabel">Statement opening</span>
              <input
                className="field"
                inputMode="decimal"
                value={opening}
                onChange={(e) => setOpening(e.target.value)}
                placeholder={(bank.reconciledBalanceCents / 100).toFixed(2)}
              />
            </label>
          )}
          <label>
            <span className="flabel">Closing balance</span>
            <input
              className="field"
              inputMode="decimal"
              value={closing}
              onChange={(e) => setClosing(e.target.value)}
              placeholder="0.00"
            />
          </label>
          <button className="btn primary" disabled={act.busy} onClick={() => void start()}>
            {changing ? 'Change' : 'Start'}
          </button>
          {changing && (
            <button className="btn soft" type="button" onClick={() => setChanging(false)}>
              Keep it as it was
            </button>
          )}
        </div>
      ) : (
        <>
          <dl className="kv">
            <dt>Statement opening</dt>
            <dd>
              <Money cents={rec.openingCents} />
            </dd>
            <dt>Statement closing</dt>
            <dd>
              <Money cents={rec.closingCents} />
            </dd>
            <dt>Cleared in Flowology ({rec.clearedCount})</dt>
            <dd>
              <Money cents={rec.clearedCents} />
            </dd>
            <dt>
              <strong>Difference</strong>
            </dt>
            <dd className={rec.differenceCents === 0 ? 'ok-text' : 'bad-text'}>
              <Money cents={rec.differenceCents} />
            </dd>
          </dl>
          <div className="row-actions">
            <button className="btn soft" onClick={() => void refresh()}>
              Recheck
            </button>
            {rec.status === 'in_progress' && !changing && (
              <button className="btn soft" type="button" onClick={change}>
                Change the statement
              </button>
            )}
            <button
              className="btn primary"
              disabled={act.busy || rec.differenceCents !== 0 || rec.status === 'finished'}
              onClick={() =>
                void (async () => {
                  const r = await act.run<Reconciliation>(
                    'bank.finishReconciliation',
                    { reconciliationId: rec.id },
                    'Reconciled.',
                  );
                  if (r) setRec(r);
                })()
              }
            >
              {rec.differenceCents === 0 ? 'Finish' : 'Finish (difference must be $0.00)'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

function ReopenReconciliation({
  id,
  through,
  onClose,
  onDone,
}: {
  id: string;
  through: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reopen = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('bank.reopenReconciliation', { reconciliationId: id, reason: reason.trim() });
      await onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={`Reopen the reconciliation through ${through}?`}
      outcome={reopenOutcome(through)}
      confirm="Reopen it"
      ready={!!reason.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void reopen()}
      onClose={onClose}
    >
      <label>
        Why (required)
        <input
          className="field"
          maxLength={300}
          value={reason}
          placeholder="The fee was matched to the wrong deposit"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

export function Banking({ held, toast, go, timeZone }: BooksProps) {
  // deposits move ServiceTitan's payments while the company mirrors it: taken and banked there (BL-088)
  const mirroring = useMirror();
  const blueprint = useDesign() === 'blueprint';
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', {});
  const zones = useQuery<{ zones: { components: { code: string }[] }[] }>('tax.listZones', {});
  // Recoverable tax on a purchase is GST or HST, as on a bill; BC PST is part of the price.
  const taxCode =
    (zones.data?.zones ?? [])
      .flatMap((z) => z.components.map((c) => c.code))
      .find((c) => c === 'HST' || c === 'GST') ?? 'GST';
  const [chosen, setChosen] = useState<string | null>(null);
  const bank = banks.data?.bankAccounts.find((b) => b.id === chosen) ?? banks.data?.bankAccounts[0];
  const open = useQuery<{ lines: BankLine[] }>(
    'bank.listLines',
    bank ? { bankAccountId: bank.id, status: 'unmatched' } : null,
  );
  const done = useQuery<{ lines: BankLine[] }>(
    'bank.listLines',
    bank ? { bankAccountId: bank.id, status: 'matched' } : null,
  );
  const act = useAct(toast, async () => {
    await Promise.all([open.reload(), done.reload(), banks.reload()]);
  });
  // A bank line is never categorised to a control account: a customer's or vendor's money is a
  // payment matched to the line, and the server refuses those accounts too. Nor to its own bank.
  const ownBank = bank?.accountId;
  const postable = useMemo(
    () => (accounts.data?.accounts ?? []).filter((a) => a.active && !isControl(a) && a.id !== ownBank),
    [accounts.data, ownBank],
  );

  if (!banks.data)
    return <Loading error={banks.error} what="the bank accounts" retry={() => void banks.reload()} />;
  if (!bank)
    return (
      <Empty>
        No bank account is set up yet.{' '}
        <button className="lnk" type="button" onClick={() => go('setup')}>
          Add one in Books setup.
        </button>
      </Empty>
    );
  return (
    <div className="books-page">
      <div className="page-head">
        {banks.data.bankAccounts.length > 1 && (
          <div className="seg" role="group" aria-label="Bank account">
            {banks.data.bankAccounts.map((b) => (
              <button
                key={b.id}
                className={`btn quiet${b.id === bank.id ? ' on' : ''}`}
                onClick={() => setChosen(b.id)}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Blueprint: the account's balances are the facts strip, as the prototype's title block says them */}
      {blueprint && (
        <div className="bp-facts flw" role="group" aria-label={bank.name}>
          <Figure label={`${bank.name}, book balance`} value={money(bank.bookBalanceCents)} />
          <Figure label="Last reconciled balance" value={money(bank.reconciledBalanceCents)} />
          <Figure
            label="To review"
            value={open.data ? String(open.data.lines.length) : '…'}
            sub={open.data?.lines.length === 1 ? 'line' : 'lines'}
          />
        </div>
      )}
      <div className="split">
        <Panel
          title="To review"
          aside={open.data ? <span className="chip">{open.data.lines.length} lines</span> : undefined}
        >
          {!open.data ? (
            <Loading error={open.error} what="the bank lines" retry={() => void open.reload()} />
          ) : open.data.lines.length === 0 ? (
            <Empty>Every line is matched.</Empty>
          ) : (
            <ul className="cards">
              {open.data.lines.map((l) => (
                <LineCard
                  key={l.id}
                  line={l}
                  accounts={postable}
                  taxCode={taxCode}
                  act={act}
                  toast={toast}
                  reload={() => Promise.all([open.reload(), done.reload(), banks.reload()])}
                />
              ))}
            </ul>
          )}
        </Panel>
        <div className="stack">
          <ReconcilePanel
            key={bank.id}
            bank={bank}
            act={act}
            toast={toast}
            mayReopen={holdsAny(held, CLOSE_PERMS)}
            onChanged={() => Promise.all([open.reload(), done.reload(), banks.reload()])}
          />
          {mirroring ? (
            <p className="muted small">Deposits: {MIRROR_LINE}</p>
          ) : (
            <RecordDeposit
              bankAccountId={bank.id}
              bankName={bank.name}
              timeZone={timeZone}
              toast={toast}
              onDone={async (deposited) => {
                // the deposit posts to the books a moment after it's recorded (QA-716): the book
                // balance is read until it has it, so the header never keeps the old one
                const id = bank.id;
                const want = bank.bookBalanceCents + deposited;
                await Promise.all([
                  open.reload(),
                  readUntil(
                    () => client.call<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {}),
                    (r) => r.bankAccounts.find((b) => b.id === id)?.bookBalanceCents === want,
                  ).then(() => banks.reload()),
                ]);
              }}
            />
          )}
          <ImportStatement
            bankAccountId={bank.id}
            bankName={bank.name}
            toast={toast}
            onDone={async () => {
              await Promise.all([open.reload(), banks.reload()]);
            }}
          />
          {!blueprint && (
            <Panel title={bank.name}>
              <dl className="kv">
                <dt>Book balance</dt>
                <dd>
                  <Money cents={bank.bookBalanceCents} />
                </dd>
                <dt>Last reconciled balance</dt>
                <dd>
                  <Money cents={bank.reconciledBalanceCents} />
                </dd>
              </dl>
            </Panel>
          )}
        </div>
      </div>
      <Panel title="Matched">
        {!done.data || done.data.lines.length === 0 ? (
          <Empty>Nothing matched yet.</Empty>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="num">Amount</th>
                  <th>
                    <span className="sr">Undo</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {done.data.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{dayText(l.postedOn)}</td>
                    <td>{l.description}</td>
                    <td className="num">
                      <Money cents={l.amountCents} />
                    </td>
                    <td>
                      {/* a wrong match comes undone until the month's reconciliation is finished (gap #11) */}
                      <button
                        className="btn quiet"
                        disabled={act.busy}
                        aria-label={`Unmatch ${l.description}`}
                        onClick={() =>
                          void act.run(
                            'bank.unmatch',
                            { bankTransactionId: l.id },
                            'Unmatched: it’s back in To review.',
                          )
                        }
                      >
                        Unmatch
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
